<template>
  <b-sidebar
    id="sidebar-retail-order-filters"
    sidebar-class="sidebar-lg"
    :visible="isFilterSidebarActive"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
    @change="(val) => $emit('update:is-filter-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          Filters
        </h5>
        <feather-icon
          class="cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- Body -->
      <div class="px-2 py-1">
        <b-form-group label="Type">
          <b-form-checkbox
            :checked="appointmentTypeCheckAll"
            :indeterminate="appointmentTypeIndeterminate"
            @change="toggleAppointmentTypeAll"
          >
            Appointment Type
          </b-form-checkbox>
          <b-form-checkbox-group
            v-model="selectedAppointmentTypes"
            class="pl-2 mt-50"
            stacked
          >
            <b-form-checkbox
              v-for="option in appointmentTypeOptions"
              :key="option"
              :value="option"
            >
              {{ option }}
            </b-form-checkbox>
          </b-form-checkbox-group>

          <b-form-checkbox
            class="mt-1"
            :checked="serviceTypeCheckAll"
            :indeterminate="serviceTypeIndeterminate"
            @change="toggleServiceTypeAll"
          >
            Service Type
          </b-form-checkbox>
          <b-form-checkbox-group
            v-model="selectedServiceTypes"
            class="pl-2 mt-50"
            stacked
          >
            <b-form-checkbox
              v-for="option in serviceTypeOptions"
              :key="option"
              :value="option"
            >
              {{ option }}
            </b-form-checkbox>
          </b-form-checkbox-group>
        </b-form-group>

        <hr>

        <b-form-group
          label="Appointment/Service Date"
          label-for="appointment-service-date"
        >
          <b-input-group>
            <flat-pickr
              id="appointment-service-date"
              v-model="appointmentServiceDate"
              placeholder="Today"
              class="form-control flat-pickr-group"
              :config="{ wrap: true, dateFormat: 'd/m/Y', disableMobile: true }"
            />
            <b-input-group-append is-text>
              <feather-icon
                icon="CalendarIcon"
                data-toggle
                size="16"
              />
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <hr>

        <b-form-group
          label="Order Date"
          label-for="order-date"
        >
          <b-input-group>
            <flat-pickr
              id="order-date"
              v-model="orderDate"
              placeholder="Today"
              class="form-control flat-pickr-group"
              :config="{ wrap: true, dateFormat: 'd/m/Y', disableMobile: true }"
            />
            <b-input-group-append is-text>
              <feather-icon
                icon="CalendarIcon"
                data-toggle
                size="16"
              />
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <b-form-group
          label="Payment Status"
          label-for="payment-status"
        >
          <v-select
            v-model="paymentStatus"
            input-id="payment-status"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="paymentStatusOptions"
            placeholder="Select from list"
          />
        </b-form-group>

        <b-form-group
          label="Appointment Status"
          label-for="appointment-status"
        >
          <v-select
            v-model="appointmentStatus"
            input-id="appointment-status"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="appointmentStatusOptions"
            placeholder="Select from list"
          />
        </b-form-group>

        <b-form-group
          label="SR Status"
          label-for="sr-status"
        >
          <v-select
            v-model="srStatus"
            input-id="sr-status"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="srStatusOptions"
            placeholder="Select from list"
          />
        </b-form-group>
      </div>

      <!-- Footer -->
      <div class="d-flex content-sidebar-footer px-2 py-1">
        <b-button
          variant="outline-primary"
          class="mr-1"
          @click="clearAll"
        >
          Clear all
        </b-button>
        <b-button
          variant="primary"
          @click="applyFilter(hide)"
        >
          Apply filter
        </b-button>
      </div>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BButton, BFormGroup, BFormCheckbox, BFormCheckboxGroup, BInputGroup, BInputGroupAppend,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'

export default {
  components: {
    BSidebar,
    BButton,
    BFormGroup,
    BFormCheckbox,
    BFormCheckboxGroup,
    BInputGroup,
    BInputGroupAppend,
    vSelect,
    flatPickr,
  },
  model: {
    prop: 'isFilterSidebarActive',
    event: 'update:is-filter-sidebar-active',
  },
  props: {
    isFilterSidebarActive: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      appointmentTypeOptions: ['Site Tours', 'Prayers', 'Pay Respects'],
      serviceTypeOptions: [
        'Removal of Longevity Label',
        'He Lu',
        'Installation of Pedestal',
        'Change of Pedestal',
        'Pedestal Engraving',
        'Bone Picking',
      ],
      paymentStatusOptions: ['Fully Paid', 'Partially Paid', 'Overpaid', 'Refunded', 'Unpaid'],
      appointmentStatusOptions: ['Awaiting Approval', 'Pending Approval', 'Confirmed', 'Completed', 'Cancelled'],
      srStatusOptions: ['Awaiting Approval', 'Pending', 'Approved', 'Completed', 'Cancelled'],

      selectedAppointmentTypes: [],
      selectedServiceTypes: [],
      appointmentServiceDate: '',
      orderDate: '',
      paymentStatus: null,
      appointmentStatus: null,
      srStatus: null,
    }
  },
  computed: {
    appointmentTypeCheckAll() {
      return this.selectedAppointmentTypes.length === this.appointmentTypeOptions.length
    },
    appointmentTypeIndeterminate() {
      return this.selectedAppointmentTypes.length > 0 && !this.appointmentTypeCheckAll
    },
    serviceTypeCheckAll() {
      return this.selectedServiceTypes.length === this.serviceTypeOptions.length
    },
    serviceTypeIndeterminate() {
      return this.selectedServiceTypes.length > 0 && !this.serviceTypeCheckAll
    },
  },
  methods: {
    toggleAppointmentTypeAll(checked) {
      this.selectedAppointmentTypes = checked ? [...this.appointmentTypeOptions] : []
    },
    toggleServiceTypeAll(checked) {
      this.selectedServiceTypes = checked ? [...this.serviceTypeOptions] : []
    },
    clearAll() {
      this.selectedAppointmentTypes = []
      this.selectedServiceTypes = []
      this.appointmentServiceDate = ''
      this.orderDate = ''
      this.paymentStatus = null
      this.appointmentStatus = null
      this.srStatus = null
    },
    applyFilter(hide) {
      this.$emit('apply-filter', {
        appointmentTypes: this.selectedAppointmentTypes,
        serviceTypes: this.selectedServiceTypes,
        appointmentServiceDate: this.appointmentServiceDate,
        orderDate: this.orderDate,
        paymentStatus: this.paymentStatus,
        appointmentStatus: this.appointmentStatus,
        srStatus: this.srStatus,
      })
      hide()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style lang="scss" scoped>
.content-sidebar-footer {
  position: sticky;
  bottom: 0;
  background-color: inherit;
  border-top: 1px solid #EBE9F1;
}
</style>
